/** Shared date helpers for the Legal OS calendar (Gregorian, Monday-based weeks). */

export interface MiniMonthCell {
  date: Date
  day: number
  currentMonth: boolean
  isSelected: boolean
  isToday: boolean
  inSelectedWeek: boolean
  hasEvent: boolean
}

export const pad2 = (n: number): string => n.toString().padStart(2, '0')

export const toIsoDate = (d: Date): string =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`

export const startOfDay = (d: Date): Date => {
  const out = new Date(d)
  out.setHours(0, 0, 0, 0)
  return out
}

export const sameCalendarDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate()

export const addCalendarDays = (d: Date, n: number): Date => {
  const out = new Date(d)
  out.setDate(out.getDate() + n)
  return out
}

/** Monday as first day of week (ISO-style). */
export const startOfWeekMonday = (d: Date): Date => {
  const out = startOfDay(d)
  const dow = out.getDay()
  const offset = dow === 0 ? -6 : 1 - dow
  out.setDate(out.getDate() + offset)
  return out
}

export const buildMiniMonthGrid = (params: {
  anchor: Date
  selected: Date
  today: Date
  highlightWeek: boolean
  datesWithEvents?: Set<string>
}): MiniMonthCell[] => {
  const { anchor, selected, today, highlightWeek, datesWithEvents } = params
  const y = anchor.getFullYear()
  const m = anchor.getMonth()
  const first = new Date(y, m, 1)
  const startOffset = (first.getDay() + 6) % 7
  const gridStart = addCalendarDays(first, -startOffset)
  const weekStart = startOfWeekMonday(selected)
  const weekEnd = addCalendarDays(weekStart, 7)

  return Array.from({ length: 42 }, (_, i) => {
    const d = addCalendarDays(gridStart, i)
    const inMonth = d.getMonth() === m
    const iso = toIsoDate(d)
    return {
      date: d,
      day: d.getDate(),
      currentMonth: inMonth,
      isSelected: sameCalendarDay(d, selected),
      isToday: sameCalendarDay(d, today),
      inSelectedWeek: highlightWeek && inMonth && d >= weekStart && d < weekEnd,
      hasEvent: inMonth && (datesWithEvents?.has(iso) ?? false)
    }
  })
}
